import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import GuestRoute from '../components/GuestRoute.jsx';
import ProtectedRoute from '../components/ProtectedRoute.jsx';
import FeatureRoute from '../components/FeatureRoute.jsx';
import RequireSubscription from '../components/RequireSubscription.jsx';
import { FEATURE_CODES } from '../constants/featureElements.js';

// Lazy load pages
const NotFoundPage = lazy(() => import('../pages/NotFound.jsx'));

// Lazy load reorganized pages
const LoginPage = lazy(() => import('../pages/Auth/Login.jsx'));
const RegisterPage = lazy(() => import('../pages/Auth/Register.jsx'));
const GoogleCallbackPage = lazy(() => import('../pages/Auth/GoogleCallback.jsx'));
const ForgotPasswordPage = lazy(() => import('../pages/Auth/ForgotPassword.jsx'));
const BreakoutToolPage = lazy(() => import('../pages/BreakoutTool/index.jsx'));
const StockDetailPage = lazy(() => import('../pages/StockDetail/index.jsx'));
const StockChartStandalonePage = lazy(() => import('../pages/StockChartStandalone/index.jsx'));

// Restored pages
const PaymentHistoryPage = lazy(() => import('../pages/PaymentHistory/index.jsx'));
const ComparePlansPage = lazy(() => import('../pages/ComparePlans/index.jsx'));
const UserSettingsPage = lazy(() => import('../pages/UserSettings/index.jsx'));

// Trang nội bộ (nginx basic auth)
const MarketExportPage = lazy(() => import('../pages/MarketExport/index.jsx'));

// Loading component
const LoadingFallback = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
    <div className="tw-text-center">
      <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-500 tw-mx-auto" />
      <p className="tw-mt-4 tw-text-gray-600">Loading...</p>
    </div>
  </div>
);

// Route configuration
const routes = [
  // Auth routes (without Layout) - GuestRoute: nếu đã login thì redirect về main
  {
    path: '/login',
    element: (
      <GuestRoute>
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage />
        </Suspense>
      </GuestRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <GuestRoute>
        <Suspense fallback={<LoadingFallback />}>
          <RegisterPage />
        </Suspense>
      </GuestRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <GuestRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ForgotPasswordPage />
        </Suspense>
      </GuestRoute>
    ),
  },
  {
    path: '/auth/google/callback',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <GoogleCallbackPage />
      </Suspense>
    ),
  },
  // Breakout Tool - Requires login + active subscription
  {
    path: '/breakout-tool',
    element: (
      <RequireSubscription>
        <Suspense fallback={<LoadingFallback />}>
          <BreakoutToolPage />
        </Suspense>
      </RequireSubscription>
    ),
  },
  // Stock Chart Standalone - Public
  {
    path: '/breakout-tool/stock-chart',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <StockChartStandalonePage />
      </Suspense>
    ),
  },
  // Stock Detail - Protected with FeatureRoute (quota checking), also requires active subscription
  {
    path: '/breakout-tool/stock/:symbol',
    element: (
      <ProtectedRoute>
        <RequireSubscription>
          <FeatureRoute feature={FEATURE_CODES.STOCK_DETAIL}>
            <Suspense fallback={<LoadingFallback />}>
              <StockDetailPage />
            </Suspense>
          </FeatureRoute>
        </RequireSubscription>
      </ProtectedRoute>
    ),
  },
  // Payment History - Protected
  {
    path: '/payment-history',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <PaymentHistoryPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Compare Plans - Protected
  {
    path: '/compare-plans',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ComparePlansPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // User Settings - Protected
  {
    path: '/user-settings',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <UserSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Xuất dữ liệu thị trường phục vụ Marketing — nội bộ, chặn bằng nginx basic auth,
  // không dùng ProtectedRoute và không có link trong menu
  {
    path: '/admin/market-export',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <MarketExportPage />
      </Suspense>
    ),
  },
  // Redirect root to breakout-tool instead of login/fintech-mock
  {
    path: '/',
    element: <Navigate to="/breakout-tool" replace />,
  },
  // Catch all - Not Found
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];

export default routes;
